import React, { useState, useMemo, useEffect, useLayoutEffect } from 'react';
import { Product } from '../data/product';
import { Card, CardHeader, CardTitle, CardFooter } from './ui/Card';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { garmentProducts } from '../data/garments';
import { DROP_LABEL } from '../shared/catalog';

// Responsive thumbnail sources. Real resized copies (FOO-320w.webp ...) are
// generated by scripts/make_responsive.py and live next to each image. The grid
// loads these small files; the original full-res file is reserved for the
// lightbox (click-to-zoom). Static hosting ignores ?w= params, so we point at
// real files instead of relying on a CDN transform.
function thumbSources(imagePath: string): { src: string; srcSet: string } {
  const base = imagePath.split('?')[0];
  const stem = base.replace(/\.(webp|jpe?g|png)$/i, '');
  const srcSet = `${stem}-320w.webp 320w, ${stem}-480w.webp 480w, ${stem}-640w.webp 640w`;
  return { src: `${stem}-640w.webp`, srcSet };
}

// Custom hook for filtered + sorted products (memoized)
function useFilteredProducts(products: Product[], opts: { showAvailableOnly: boolean; priceRange: [number,number] | null; search: string; sort: 'new'|'old'; }) {
  return useMemo(() => {
    let list = products.slice();
    if (opts.showAvailableOnly) list = list.filter(p => !p.soldOut);
    if (opts.priceRange) {
      const [min,max] = opts.priceRange;
      const toNumber = (price: string) => parseFloat(price.replace(/[^0-9.]/g,''));
      list = list.filter(p => { const val = toNumber(p.price); return val >= min && val <= max; });
    }
    if (opts.search) {
      const q = opts.search.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q) || (p.description||'').toLowerCase().includes(q));
    }
  // Sort by releaseRank when present to group new drops, then by id
  list.sort((a,b) => {
    const ar = a.releaseRank ?? 0;
    const br = b.releaseRank ?? 0;
    if (opts.sort === 'new') {
      if (br !== ar) return br - ar; // higher rank first
      return b.id.localeCompare(a.id);
    } else {
      if (ar !== br) return ar - br; // lower rank first
      return a.id.localeCompare(b.id);
    }
  });
    return list;
  }, [products, opts.showAvailableOnly, opts.priceRange, opts.search, opts.sort]);
}

const ProductGrid: React.FC = () => {
  const { addToCart, items } = useCart();
  const { addToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  // Persist key for filters
  const FILTER_KEY = 'filters_last_v1';

  // Inventory state (priceId => soldOut)
  const [soldMap, setSoldMap] = useState<Record<string, boolean>>({});
  useEffect(() => {
    let aborted = false;
    fetch('/api/inventory', { headers: { 'Accept': 'application/json' } })
      .then(r => (r.ok ? r.json() : null))
      .then((data: Record<string, boolean> | null) => {
        if (!aborted && data) setSoldMap(data);
      })
      .catch((error) => {
        console.error('Inventory fetch failed:', error);
      });
    return () => { aborted = true; };
  }, []);

  // Parse initial state from URL
  const params = new URLSearchParams(location.search);
  const initialAvail = params.get('instock') === '1';
  const initialPrice = params.get('price'); // e.g. "0-40"
  const initialSearch = params.get('q') || '';

  const [sort, setSort] = useState<'new'|'old'>('new');
  const [showAvailableOnly, setShowAvailableOnly] = useState(initialAvail);
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [priceRange, setPriceRange] = useState<[number, number] | null>(() => initialPrice ? (initialPrice.split('-').map(Number) as [number,number]) : null);
  const [search, setSearch] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [addedRecently, setAddedRecently] = useState<string | null>(null);

  // Per-product image index (for front/back carousel)
  const [slideIdx, setSlideIdx] = useState<Record<string, number>>({});
  const [lightbox, setLightbox] = useState<{ id: string; index: number } | null>(null);

  // Restore last filters (if no URL params) within 24h
  useLayoutEffect(() => {
    if(location.search) return; // explicit params take precedence
    try {
      const raw = localStorage.getItem(FILTER_KEY);
      if(raw){
        const data = JSON.parse(raw);
        if(Date.now() - data.ts < 86400000){
          if(typeof data.showAvailableOnly === 'boolean') setShowAvailableOnly(data.showAvailableOnly);
          if(Array.isArray(data.priceRange)) setPriceRange(data.priceRange);
          if(typeof data.search === 'string') { setSearch(data.search); setDebouncedSearch(data.search); }
        }
      }
    } catch(_){}}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  , []);
  // Persist filters when they change
  useEffect(() => {
    try { localStorage.setItem(FILTER_KEY, JSON.stringify({ showAvailableOnly, priceRange, search: debouncedSearch, ts: Date.now() })); } catch(_){}
  }, [showAvailableOnly, priceRange, debouncedSearch]);

  // Debounce search input
  useEffect(() => { const t = setTimeout(()=> setDebouncedSearch(search), 300); return () => clearTimeout(t); }, [search]);

  // Sync state to URL (without full reload)
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if(showAvailableOnly) p.set('instock','1'); else p.delete('instock');
    if(priceRange) p.set('price', `${priceRange[0]}-${priceRange[1]}`); else p.delete('price');
    if(debouncedSearch) p.set('q', debouncedSearch); else p.delete('q');
    const newQuery = p.toString();
    const newUrl = newQuery ? `?${newQuery}` : '';
    if(newUrl !== location.search) navigate({ search: newUrl }, { replace: true });
  }, [showAvailableOnly, priceRange, debouncedSearch, location.search, navigate]);

  const closeFilters = () => setOpenFilter(null);

  // Apply inventory (soldMap) to base products
  const products: Product[] = useMemo(() => {
    return garmentProducts.map(p => ({
      ...p,
      soldOut: p.priceId ? !!soldMap[p.priceId] : !!p.soldOut,
    }));
  }, [soldMap]);

  const filteredProducts = useFilteredProducts(products, { showAvailableOnly, priceRange, search: debouncedSearch, sort });

  const handleAdd = (p: Product) => {
    if (p.soldOut) { addToast('Sold out', 'info'); return; }
    if(items.some(i=> i.id===p.id)) { addToast('Already in cart','info'); return; }
    addToCart(p);
    addToast('Added to cart','success');
    setAddedRecently(p.id);
  // Open mini cart after adding
  try { window.dispatchEvent(new CustomEvent('open-mini-cart')); } catch {}
    setTimeout(() => { setAddedRecently(prev => prev === p.id ? null : prev); }, 1400);
  };

  // Onboarding toasts (once)
  useEffect(() => {
    try {
      if(location.pathname !== '/' ) return;
      if(localStorage.getItem('onboard_toast_shown')) return;
      localStorage.setItem('onboard_toast_shown','1');
      addToast('Use filters above','info');
    } catch(_){}
  }, [addToast, location.pathname]);

  // Scroll-reveal for product cards (motion-safe)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;
    const cards = Array.from(document.querySelectorAll<HTMLElement>('.catalog-card'));
    if (!cards.length) return;
    // Initialize state
    cards.forEach(el => el.classList.add('reveal'));
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add('reveal-in');
          io.unobserve(e.target);
        }
      }
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });
    cards.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [filteredProducts]);

  // Confetti palette cycle per session
  useEffect(() => {
    const palettes: string[][] = [
      ['#ffcc00','#ff4d67','#3fd1ff','#8fff4d'],
      ['#ffd166','#ef476f','#06d6a0','#26547c'],
      ['#ffb3c1','#ff6392','#7fc8f8','#83e377'],
      ['#f6d365','#fda085','#c3f584','#7dd3fc']
    ];
    try {
      const sessionKey = 'confetti_palette_session';
      let paletteIndex: number;
      const sessionStored = sessionStorage.getItem(sessionKey);
      if(sessionStored){ paletteIndex = parseInt(sessionStored,10) || 0; }
      else {
        const lsKey = 'confetti_palette_index';
        const prev = parseInt(localStorage.getItem(lsKey)||'-1',10);
        paletteIndex = (isNaN(prev)?0:prev+1) % palettes.length;
        localStorage.setItem(lsKey, String(paletteIndex));
        sessionStorage.setItem(sessionKey, String(paletteIndex));
      }
      const palette = palettes[paletteIndex];
      palette.forEach((col, i) => document.documentElement.style.setProperty(`--confetti${i+1}`, col));
    } catch(_){}
  }, []);

  // Idle pulse effect
  useEffect(() => {
    if(typeof window === 'undefined') return;
    if(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let idleTimer: number | null = null;
    const idleDelay = 30000; // 30s
    const reset = () => { if(idleTimer) clearTimeout(idleTimer); idleTimer = window.setTimeout(trigger, idleDelay); };
    const trigger = () => {
      const cards = document.querySelectorAll<HTMLDivElement>('.catalog-card');
      if(cards.length){
        const card = cards[Math.floor(Math.random()*cards.length)];
        card.classList.add('idle-pulse');
        const remove = () => { card.classList.remove('idle-pulse'); card.removeEventListener('animationend', remove); };
        card.addEventListener('animationend', remove);
      }
      reset();
    };
    ['pointermove','keydown','scroll','touchstart','visibilitychange'].forEach(evt => window.addEventListener(evt, reset, { passive:true }));
    reset();
    return () => { if(idleTimer) clearTimeout(idleTimer); ['pointermove','keydown','scroll','touchstart','visibilitychange'].forEach(evt => window.removeEventListener(evt, reset)); };
  }, []);

  const productSchema = useMemo(() => {
    return filteredProducts.map(p => ({
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: p.name,
      image: p.image,
      description: p.description,
      sku: p.id,
      offers: {
        '@type': 'Offer',
        priceCurrency: 'USD',
        price: parseFloat(p.price.replace(/[^0-9.]/g,'')) || 0,
        availability: p.soldOut ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock'
      }
    }));
  }, [filteredProducts]);

  // Prefetch a few likely next images (first 4) when browser idle
  useEffect(() => {
    const prefetch = () => {
      const seen = new Set<string>();
      filteredProducts.slice(0, 4).forEach(p => {
        const list = p.images && p.images.length ? p.images : [p.image];
        list.forEach(url => {
          const href = thumbSources(url).src; // prefetch the same thumbnail the grid loads
          if (seen.has(href)) return; seen.add(href);
          const link = document.createElement('link');
          link.rel = 'prefetch';
          link.as = 'image';
          link.href = href;
          document.head.appendChild(link);
        });
      });
    };
    if('requestIdleCallback' in window){
      (window as any).requestIdleCallback(prefetch, { timeout: 2500 });
    } else {
      setTimeout(prefetch, 1200);
    }
  }, [filteredProducts]);

  // Lightbox keyboard navigation
  useEffect(() => {
    if(!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      const prod = filteredProducts.find(pp => pp.id === lightbox.id);
      const len = prod?.images?.length || 1;
      if(e.key === 'Escape') setLightbox(null);
      else if(e.key === 'ArrowRight') setLightbox(lb => lb ? { ...lb, index: (lb.index + 1) % len } : lb);
      else if(e.key === 'ArrowLeft') setLightbox(lb => lb ? { ...lb, index: (lb.index - 1 + len) % len } : lb);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, filteredProducts]);

  return (
    <div>
      {/* Overlay for modal dropdowns */}
      {openFilter && <div className="filter-overlay" onClick={closeFilters} />}
      <div className="catalog-header">
  <h2 className="catalog-title">T-SHIRTS <span className="drop-badge" aria-label="Seasonal drop">{DROP_LABEL}</span></h2>
        <p className="catalog-sub">Hand-printed garments on thrifted clothing.</p>
        <div className="catalog-bogo" role="note" aria-label="Buy one get one free — the cheaper item in every pair is free, applied automatically at checkout">
          <div className="ticker-track" aria-hidden="true">
            {[0,1].map(g => (
              <span className="ticker-group" key={g}>
                BUY ONE GET ONE FREE&nbsp;&nbsp;★&nbsp;&nbsp;CHEAPER ITEM IN EVERY PAIR IS FREE&nbsp;&nbsp;★&nbsp;&nbsp;APPLIED AT CHECKOUT&nbsp;&nbsp;★&nbsp;&nbsp;
              </span>
            ))}
          </div>
        </div>
        <div className="catalog-bar">
          <div className="catalog-filters">
            <button className={"filter-btn" + (openFilter==='availability' ? ' active' : '')} onClick={() => setOpenFilter(openFilter==='availability'?null:'availability')}>
              Availability ▾
            </button>
          </div>
          <div className="catalog-search">
            <input value={search} onChange={e=> setSearch(e.target.value)} placeholder="Search" aria-label="Search products" />
          </div>
          <div className="catalog-sort">
            <label>Sort by:&nbsp;</label>
            <select value={sort} onChange={e=> setSort(e.target.value as any)}>
              <option value="new">Date, new to old</option>
              <option value="old">Date, old to new</option>
            </select>
            <span className="count">{filteredProducts.length} products</span>
          </div>
        </div>
        {/* Dropdown Panels */}
        <div className="filter-panels">
          {openFilter==='availability' && (
            <div className="filter-panel" tabIndex={-1} role="dialog" aria-label="Availability filter">
              <label className="checkbox-row">
                <input type="checkbox" checked={showAvailableOnly} onChange={() => setShowAvailableOnly(v=>!v)} /> In stock only
              </label>
              <button className="apply-btn" onClick={closeFilters}>Done</button>
            </div>
          )}
          {openFilter==='price' && (
            <div className="filter-panel" tabIndex={-1} role="dialog" aria-label="Price filter">
              <div className="price-options">
                <button onClick={()=> setPriceRange([0,40])} className={priceRange?.[1]===40? 'selected':''}>Under $40</button>
                <button onClick={()=> setPriceRange([40,80])} className={priceRange?.[0]===40? 'selected':''}>$40–$80</button>
                <button onClick={()=> setPriceRange([80,120])} className={priceRange?.[0]===80? 'selected':''}>$80–$120</button>
                <button onClick={()=> setPriceRange(null)} className={!priceRange? 'selected':''}>Any</button>
              </div>
              <button className="apply-btn" onClick={closeFilters}>Done</button>
            </div>
          )}
        </div>
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }} />
      <div className="catalog-grid">
        {filteredProducts.map(p => {
          const inCart = items.some(i=> i.id===p.id);
          const imgs = p.images && p.images.length ? p.images : [p.image];
          const idx = slideIdx[p.id] ?? 0;
          const current = imgs[Math.max(0, Math.min(idx, imgs.length-1))];
          const { src: srcBase, srcSet } = thumbSources(current);
          const sizes = '(max-width: 600px) 50vw, (max-width: 900px) 33vw, 200px';
          const displayName = p.name.replace(/_/g, ' ');
          return (
          <Card key={p.id} className="catalog-card">
            <div
              className="thumb-wrap"
              onMouseEnter={() => { if (imgs.length > 1) setSlideIdx(prev => ({ ...prev, [p.id]: 1 })); }}
              onMouseLeave={() => { if (imgs.length > 1) setSlideIdx(prev => ({ ...prev, [p.id]: 0 })); }}
            >
              {/* Hover to swap (desktop); click opens lightbox */}
              <img
                onClick={() => setLightbox({ id: p.id, index: idx })}
                src={srcBase}
                srcSet={srcSet}
                sizes={sizes}
                alt={displayName}
                loading="lazy"
                decoding="async"
                width={600}
                height={600}
                style={p.thumbFit === 'contain' ? { objectFit: 'contain', backgroundColor: '#fff' } : undefined}
              />
              {p.soldOut
                ? <span className="sold-badge">Sold out</span>
                : <span className="one-of-one-badge" title="One of one — the only one that exists">1 of 1</span>}
            </div>
            <CardHeader className="tight-head">
              <CardTitle className="prod-name">{displayName}</CardTitle>
              {p.fitNote && (
                <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                  {p.fitNote}
                </div>
              )}
            </CardHeader>
            <CardFooter className="tight-foot">
              <div className="purchase-row">
                <span className="price">{p.price}</span>
                {!p.soldOut && (
                  <div className="add-controls">
                    <button
                      aria-label={inCart ? `${p.name} already in cart` : `Add ${p.name} to cart`}
                      className={"add-btn" + (addedRecently===p.id ? ' added' : '')}
                      onClick={() => handleAdd(p)}
                      disabled={inCart || addedRecently===p.id}
                      title="Add to cart"
                    >
                      {inCart ? 'In Cart' : (addedRecently===p.id ? 'Added ✓' : 'Add to Cart')}
                      {addedRecently===p.id && !inCart && (
                        <span className="confetti-set" aria-hidden="true">
                          <span></span><span></span><span></span><span></span>
                        </span>
                      )}
                    </button>
                  </div>
                )}
                {p.soldOut && (
                  <div className="add-controls">
                    <button className="add-btn" disabled aria-disabled="true" title="Sold out">Sold out</button>
                  </div>
                )}
              </div>
            </CardFooter>
          </Card>
        )})}
      </div>

      {lightbox && (() => {
        const prod = filteredProducts.find(pp => pp.id === lightbox.id);
        if(!prod) return null;
        const imgs = prod.images && prod.images.length ? prod.images : [prod.image];
        const idx = Math.max(0, Math.min(lightbox.index, imgs.length-1));
        
        const rawUrl = imgs[idx];
        // Lightbox uses the original full-res file for click-to-zoom.
        const optimizedUrl = rawUrl.split('?')[0];
        
        const prev = (e: React.MouseEvent) => { 
          e.stopPropagation(); 
          setLightbox(lb => lb ? { ...lb, index: (lb.index - 1 + imgs.length) % imgs.length } : lb); 
        };
        const next = (e: React.MouseEvent) => { 
          e.stopPropagation(); 
          setLightbox(lb => lb ? { ...lb, index: (lb.index + 1) % imgs.length } : lb); 
        };
        const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
          if (e.target === e.currentTarget) setLightbox(null);
        };

        return (
          <div className="lightbox" role="dialog" aria-modal="true" onClick={onBackdropClick}>
            <div
              className="lightbox-inner"
              onClick={(e)=> e.stopPropagation()}
              onMouseDown={(e)=> e.stopPropagation()}
              onPointerDown={(e)=> e.stopPropagation()}
            >
              <button className="lightbox-close" aria-label="Close" onClick={(e)=>{ e.stopPropagation(); setLightbox(null); }}>×</button>
              {imgs.length > 1 && (
                <button
                  className="lightbox-arrow left"
                  aria-label="Previous image"
                  onClick={prev}
                  onMouseDown={(e)=> e.stopPropagation()}
                  onPointerDown={(e)=> e.stopPropagation()}
                >
                  ‹
                </button>
              )}
              
              {/* Optimized image with loading state */}
              <img
                src={optimizedUrl}
                alt={`${prod.name} view ${idx+1}`}
                onClick={e => e.stopPropagation()}
                onMouseDown={e => e.stopPropagation()}
                onPointerDown={e => e.stopPropagation()}
                style={{ 
                  maxWidth: '90vw', 
                  maxHeight: '85vh',
                  transition: 'opacity 0.2s ease',
                }}
                loading="eager"
                decoding="async"
              />
              
              {/* Preload adjacent images for faster navigation */}
              {imgs.length > 1 && (
                <>
                  <link
                    rel="preload"
                    as="image"
                    href={imgs[(idx + 1) % imgs.length].split('?')[0]}
                  />
                  <link
                    rel="preload"
                    as="image"
                    href={imgs[(idx - 1 + imgs.length) % imgs.length].split('?')[0]}
                  />
                </>
              )}
              
              {imgs.length > 1 && (
                <button
                  className="lightbox-arrow right"
                  aria-label="Next image"
                  onClick={next}
                  onMouseDown={(e)=> e.stopPropagation()}
                  onPointerDown={(e)=> e.stopPropagation()}
                >
                  ›
                </button>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ProductGrid;
